"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { formatDistanceToNow } from "date-fns"
import { Loader2, Check, X } from "lucide-react"

interface Application {
  _id: string
  user: string
  status: string
  message: string
  appliedAt: string
  userData?: {
    _id: string
    name: string
    userType: string
    location?: string
    profileImage?: string
  }
}

interface JobApplicationsListProps {
  jobId: string
  applications: Application[]
}

export default function JobApplicationsList({ jobId, applications }: JobApplicationsListProps) {
  const [applicationsData, setApplicationsData] = useState<Application[]>(applications)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  // Fetch applicant details
  useEffect(() => {
    const fetchApplicantDetails = async () => {
      try {
        setIsLoading(true)
        const response = await fetch(`/api/jobs/${jobId}/applications`)

        if (!response.ok) {
          throw new Error("Failed to fetch applications")
        }

        const data = await response.json()
        setApplicationsData(data.applications)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load applications")
      } finally {
        setIsLoading(false)
      }
    }

    fetchApplicantDetails()
  }, [jobId])

  const handleUpdateStatus = async (applicationId: string, newStatus: string) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/applications/${applicationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      })

      if (!response.ok) {
        throw new Error("Failed to update application status")
      }

      // Update the local state
      setApplicationsData((prev) =>
        prev.map((app) => (app._id === applicationId ? { ...app, status: newStatus } : app)),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update application")
    }
  }

  if (isLoading) {
    return (
      <div className="mt-3 text-center py-4">
        <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" />
        <p className="mt-2 text-gray-500">Loading applications...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">{error}</p>
      </div>
    )
  }

  if (applicationsData.length === 0) {
    return (
      <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-md text-center">
        <p className="text-gray-500">No applications received yet.</p>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-4">
      {applicationsData.map((application) => (
        <div key={application._id} className="border border-gray-200 rounded-md p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-bold">
                  {application.userData?.name ? application.userData.name.charAt(0).toUpperCase() : "?"}
                </div>
              </div>
              <div className="ml-3">
                <Link
                  href={`/profile/${application.userData?._id || application.user}`}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  {application.userData?.name || "Applicant"}
                </Link>
                <p className="text-xs text-gray-500">
                  Applied{" "}
                  {application.appliedAt
                    ? formatDistanceToNow(new Date(application.appliedAt), { addSuffix: true })
                    : "recently"}
                </p>
                <div className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{application.message}</div>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              {application.status === "pending" ? (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleUpdateStatus(application._id, "accepted")}
                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(application._id, "rejected")}
                    className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Reject
                  </button>
                </div>
              ) : (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    application.status === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  }`}
                >
                  {application.status === "accepted" ? "Accepted" : "Rejected"}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

